import { useRouter } from '@tanstack/react-router';
import LoadingDots from '../LoadingDots';

export default function Button({
  children,
  className,
  helpButton,
  linkTo,
  onClick,
  style,
  pulsating,
  size,
  block,
  type = 'primary',
  icon,
  disabled,
  id,
  loading = false,
}: {
  children: React.ReactNode;
  className?: string;
  helpButton?: boolean;
  linkTo?: string;
  onClick?: () => Promise<void> | void;
  style?: React.CSSProperties;
  pulsating?: boolean;
  size?: 'large' | 'medium' | 'small';
  block?: boolean;
  type?: 'primary' | 'default' | 'unset';
  icon?: React.ReactNode;
  disabled?: boolean;
  id?: string;
  loading?: boolean;
}) {
  const router = useRouter();

  const handleClick = async () => {
    if (linkTo) {
      router.navigate({ to: linkTo });
    } else if (onClick) {
      await onClick();
    }
  };

  const isDisabled = disabled || loading;

  return (
    <button
      onClick={async (e) => {
        e.preventDefault();

        if (isDisabled) return;

        await handleClick();
      }}
      className={`
        flex justify-center items-center gap-2 rounded-xl text-center relative
        ${type === 'primary' ? 'bg-primary' : type === 'default' ? 'bg-text-main' : ''}
        ${type === 'primary' || type === 'default' ? 'text-white' : ''}
        ${size === 'large' ? 'px-6 py-3' : size === 'medium' ? 'px-4 py-2' : 'px-3 py-1'}
        ${pulsating && !isDisabled ? 'pulsating-btn' : ''}
        ${size === 'large' ? 'text-body lg:text-title' : size === 'medium' ? 'text-body' : 'text-body'}
        ${className}
        ${block ? 'w-full' : ''}
        ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      disabled={isDisabled}
      style={{
        ...style,
        fontWeight: 'bold',
        backgroundColor: helpButton ? 'var(--color-primary)' : undefined,
        border: helpButton ? '1px solid var(--color-primary-dark)' : undefined,
      }}
      id={id}
    >
      {loading ? (
        <div className="flex justify-center items-center w-full py-1">
          <LoadingDots
            size={size === 'small' ? 'sm' : 'md'}
            className={type === 'primary' || type === 'default' ? '[&>div]:bg-white' : ''}
          />
        </div>
      ) : (
        <>
          {children}
          {icon}
        </>
      )}
    </button>
  );
}
